import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import subcontractorSpecialtiesService from '../../services/subcontractorSpecialtiesService';
import { formatDate } from '../../utils/formatDate';

// ─── Style tokens ─────────────────────────────────────────────────────────────
// Typographie de la fiche OnSiteBid, valeur pour valeur : encre #2C3E50, gris
// #6B7280, separateurs rgba(45,55,72,0.12), texte 12 px, libelles 11 px,
// champs de 32 px. Seules les couleurs de marque restent celles de PM (orange).
// L'interligne est ecrit sur CHAQUE element de texte : PM force
// `* { line-height: normal }`, qui empeche tout heritage depuis la racine.
const inkCls = 'text-[#2C3E50]';
const mutedCls = 'text-[#6B7280]';
const lineCls = 'border-[rgba(45,55,72,0.12)]';
const fieldBaseCls =
  `w-full px-3 text-xs leading-4 border ${lineCls} rounded-lg bg-white ${inkCls} ` +
  'placeholder:text-gray-400 transition-colors disabled:opacity-60 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
const inputCls = `h-8 ${fieldBaseCls}`;
const textareaCls = `py-2 ${fieldBaseCls} resize-none`;
// Libelle d'un champ, ou titre d'une section.
const titleCls = `text-xs leading-4 font-semibold ${inkCls}`;
const labelCls = `block ${titleCls} mb-1.5`;
// Libelle d'une valeur en lecture.
const valueLabelCls = `text-[11px] leading-[16.5px] ${mutedCls}`;
const textCls = `text-xs leading-4 ${inkCls}`;
const noteCls = `text-xs leading-4 ${mutedCls}`;
const gridCls = 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3';
const errorCls = 'mt-1.5 text-xs leading-4 text-[#E53E3E]';
const requiredCls = 'text-[#E53E3E]';
const buttonCls =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap h-7 px-3 text-xs leading-4 font-medium ' +
  'rounded-lg border transition-colors disabled:opacity-50 disabled:pointer-events-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20';
const outlineButtonCls = `${buttonCls} ${lineCls} bg-white ${inkCls} hover:bg-[#F7FAFC]`;
const primaryButtonCls = `${buttonCls} border-primary bg-primary text-white hover:bg-primary-dark hover:border-primary-dark`;

// Pastilles : memes forme et couleurs que dans OnSiteBid ; « brand » = orange PM.
const PILL_VARIANTS = {
  green: { badge: 'border-[#C6EBD3] bg-[#E9F9EF] text-[#15803D]', dot: 'bg-[#22C55E]' },
  grey: { badge: 'border-[#E5E7EB] bg-[#F1F3F5] text-[#4B5563]', dot: 'bg-[#9CA3AF]' },
  brand: { badge: 'border-primary/20 bg-primary/10 text-primary', dot: 'bg-primary' },
};

// value = valeur persistee en base (inchangee) ; key = libelle affiche
const CONTACT_ROLES = [
  { value: 'Comptabilité',  key: 'accounting' },
  { value: 'Gestion projet', key: 'projectManagement' },
  { value: 'Chargé projet',  key: 'projectLead' },
  { value: 'Coordonnateur',  key: 'coordinator' },
  { value: 'Autre',          key: 'other' },
];

// Convention partagee avec OnSiteBid : « Active » / « Inactive » en base.
export const SUBCONTRACTOR_STATUSES = [
  { code: 'Active', key: 'statusActive' },
  { code: 'Inactive', key: 'statusInactive' },
];

/** « active », « ACTIVE »… → « Active ». Un statut inconnu reste tel quel. */
export const subcontractorStatusCode = (status) => {
  const value = String(status ?? '')?.trim();
  return (
    SUBCONTRACTOR_STATUSES?.find((item) => item?.code?.toLowerCase() === value?.toLowerCase())?.code ?? value
  );
};

const Pill = ({ children, variant = 'grey' }) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs leading-4 font-semibold ${PILL_VARIANTS?.[variant]?.badge}`}
  >
    <span className={`w-1.5 h-1.5 flex-shrink-0 rounded-full ${PILL_VARIANTS?.[variant]?.dot}`} />
    {children}
  </span>
);

const Section = ({ title, hint, action, children }) => (
  <section className={`space-y-2.5 border-t ${lineCls} pt-4 first:border-t-0 first:pt-0`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h4 className={titleCls}>{title}</h4>
        {hint ? <p className={`mt-0.5 ${noteCls}`}>{hint}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

/** Une valeur en lecture : son libelle au-dessus, « — » si elle est vide. */
const Value = ({ label, children, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <dt className={valueLabelCls}>{label}</dt>
    <dd className={`${textCls} whitespace-pre-wrap break-words`}>
      {children || <span className={mutedCls}>—</span>}
    </dd>
  </div>
);

// ─── File Upload Widget ───────────────────────────────────────────────────────
const DocUploadWidget = ({ label, docPath, signedUrl, onUpload, onRemove, uploading, readOnly = false }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const filename = docPath ? docPath?.split('/')?.pop() : null;

  return (
    <div>
      <label className={readOnly ? `block ${valueLabelCls} mb-1` : labelCls}>{label}</label>
      {filename ? (
        <div className={`flex items-center gap-2 h-8 px-3 border ${lineCls} rounded-lg bg-[#F7FAFC] ${textCls}`}>
          <Icon name="FileText" size={14} className={`${mutedCls} flex-shrink-0`} />
          <span className="truncate flex-1">{filename}</span>
          {signedUrl && (
            <a
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium underline underline-offset-2 flex-shrink-0"
            >
              {t('directory.modal.viewFile')}
            </a>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={onRemove}
              className="text-[#E53E3E] hover:underline flex-shrink-0"
            >
              {t('directory.modal.removeFile')}
            </button>
          )}
        </div>
      ) : readOnly ? (
        <p className={noteCls}>—</p>
      ) : (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const file = e?.target?.files?.[0];
              if (file) onUpload(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef?.current?.click()}
            disabled={uploading}
            className={`flex items-center gap-2 h-8 px-3 border border-dashed ${lineCls} rounded-lg ${noteCls} hover:border-primary hover:text-primary transition-colors w-full`}
          >
            {uploading ? (
              <Icon name="Loader2" size={14} className="animate-spin" />
            ) : (
              <Icon name="Upload" size={14} />
            )}
            <span>{uploading ? 'Téléversement...' : 'Choisir un fichier'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Fiche d'un sous-traitant, dans une fenetre : la meme que celle d'OnSiteBid
 * (memes sections, meme ordre). Lecture d'abord (`isView`), puis « Modifier »
 * rend les champs modifiables ; sert aussi a « Ajouter un sous-traitant »
 * (`record` vide). Affichage seulement : l'etat du formulaire, les documents,
 * les contacts et l'enregistrement restent dans la page Repertoire.
 * Les specialites sont lues ici, en LECTURE SEULE.
 */
const SubcontractorCard = ({
  record,
  isView,
  form,
  errors,
  saving,
  onChange,
  trades,
  tradeLabels,
  rbqSignedUrl,
  insuranceSignedUrl,
  uploadingRbq,
  uploadingInsurance,
  onDocUpload,
  onDocRemove,
  contacts,
  onAddContact,
  onUpdateContact,
  onRemoveContact,
  onClose,
  onEdit,
  onCancel,
  onSave,
}) => {
  const { t, language } = useTranslation();
  const m = (key) => t(`directory.modal.${key}`);

  // Specialites de la fiche : undefined = lecture en cours, null = echec.
  const [specialties, setSpecialties] = useState(undefined);
  const recordId = record?.id || null;
  useEffect(() => {
    if (!recordId) return undefined;
    let active = true;
    setSpecialties(undefined);
    subcontractorSpecialtiesService?.getSummary(recordId)?.then((summary) => {
      if (active) setSpecialties(summary);
    });
    return () => {
      active = false;
    };
  }, [recordId]);

  const tradeName = (trade) => tradeLabels?.[trade] || trade;
  const day = (value) => (value ? formatDate(value, language) : '');
  const statusCode = subcontractorStatusCode(isView ? record?.status : form?.status);
  const statusKey = SUBCONTRACTOR_STATUSES?.find((item) => item?.code === statusCode)?.key;
  const shownContacts = contacts?.filter((c) => !c?._delete) || [];

  const field = (id, label, key, { type = 'text', placeholder, required = false } = {}) => (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label} {required && <span className={requiredCls}>*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={form?.[key] ?? ''}
        disabled={saving}
        aria-invalid={Boolean(errors?.[key])}
        onChange={(e) => onChange(key, e?.target?.value)}
        className={inputCls}
        placeholder={placeholder}
      />
      {errors?.[key] ? <p className={errorCls}>{errors?.[key]}</p> : null}
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm antialiased leading-[1.5] ${inkCls}`}
      onClick={(e) => {
        if (e?.target === e?.currentTarget) onClose();
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[640px] max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {/* ── En-tete ── */}
        <div className={`border-b ${lineCls} px-6 py-4 pr-12`}>
          <div className="min-w-0">
            {isView ? (
              <h3 className={`text-base leading-6 font-semibold ${inkCls} break-words`}>{record?.company_name}</h3>
            ) : (
              <>
                {/* Fiche existante : titre pour les lecteurs d'ecran seulement, comme dans OnSiteBid. */}
                <h3 className={record ? 'sr-only' : `text-base leading-6 font-semibold ${inkCls} mb-3`}>
                  {record ? m('titleEdit') : m('titleAdd')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
                  {field('fiche-nom', m('companyName'), 'company_name', {
                    placeholder: m('companyNamePlaceholder'),
                    required: true,
                  })}
                  <div>
                    <label htmlFor="fiche-statut" className={labelCls}>
                      {m('status')}
                    </label>
                    <select
                      id="fiche-statut"
                      value={statusCode}
                      disabled={saving}
                      onChange={(e) => onChange('status', e?.target?.value)}
                      className={inputCls}
                    >
                      {/* Un statut absent ou inconnu reste affiche tel quel tant qu'on n'en choisit pas un autre. */}
                      {statusKey ? null : <option value={statusCode}>{statusCode || '—'}</option>}
                      {SUBCONTRACTOR_STATUSES?.map((item) => (
                        <option key={item?.code} value={item?.code}>
                          {m(item?.key)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
            {record ? (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {isView && statusCode ? (
                  <Pill variant={statusCode === 'Active' ? 'green' : 'grey'}>
                    {statusKey ? m(statusKey) : statusCode}
                  </Pill>
                ) : null}
                <Pill variant="brand">{record?.source === 'bid' ? 'OnSiteBid' : 'OnSitePM'}</Pill>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={m('close')}
            className={`absolute right-4 top-4 rounded-lg p-1 ${mutedCls} hover:bg-[#F7FAFC] hover:text-[#2C3E50] transition-colors`}
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* ── Corps ── */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Specialites : fiche existante seulement, en lecture seule */}
          {record ? (
            <Section title={m('sectionSpecialties')} hint={isView ? undefined : m('specialtiesEditHint')}>
              {specialties === undefined ? (
                <p className={noteCls}>{m('specialtiesLoading')}</p>
              ) : specialties === null ? (
                <p className={noteCls}>{m('specialtiesLoadError')}</p>
              ) : (
                <dl className={gridCls}>
                  <Value label={m('specialtyPrimary')}>{specialties?.primary || m('specialtyUndefined')}</Value>
                  <Value label={m('specialtyOthers')}>{specialties?.others?.join('\n')}</Value>
                </dl>
              )}
            </Section>
          ) : null}

          <Section title={m('trade')}>
            {isView ? (
              <dl className={gridCls}>
                <Value label={m('tradeLabel')}>{form?.trade ? tradeName(form?.trade) : ''}</Value>
              </dl>
            ) : (
              <div className={gridCls}>
                <div>
                  <label htmlFor="fiche-metier" className={labelCls}>
                    {m('tradeLabel')} <span className={requiredCls}>*</span>
                  </label>
                  <select
                    id="fiche-metier"
                    value={form?.trade}
                    disabled={saving}
                    onChange={(e) => onChange('trade', e?.target?.value)}
                    className={inputCls}
                  >
                    <option value="">{m('tradeSelectPlaceholder')}</option>
                    {/* Un metier desactive depuis reste affiche tant qu'on n'en choisit pas un autre. */}
                    {form?.trade && !trades?.includes(form?.trade) ? (
                      <option value={form?.trade}>{tradeName(form?.trade)}</option>
                    ) : null}
                    {trades?.map((trade) => (
                      <option key={trade} value={trade}>{tradeName(trade)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </Section>

          <Section title={m('contactPerson')}>
            {isView ? (
              <dl className={gridCls}>
                <Value label={m('contactName')}>{form?.contactPerson}</Value>
                <Value label={m('contactPhone')}>{form?.phone}</Value>
                <Value label={m('contactEmail')} wide>{form?.email}</Value>
              </dl>
            ) : (
              <div className={gridCls}>
                {field('fiche-contact', m('contactName'), 'contactPerson', { placeholder: m('contactPersonPlaceholder') })}
                {field('fiche-telephone', m('contactPhone'), 'phone', { type: 'tel', placeholder: m('phonePlaceholder') })}
                <div className="sm:col-span-2">
                  {field('fiche-courriel', m('contactEmail'), 'email', { type: 'email', placeholder: m('emailPlaceholder') })}
                </div>
              </div>
            )}
          </Section>

          <Section title={m('sectionEstimation')} hint={m('estimationHint')}>
            {isView ? (
              <dl className={gridCls}>
                <Value label={m('contactName')}>{form?.estName}</Value>
                <Value label={m('contactPhone')}>{form?.estPhone}</Value>
                <Value label={m('contactEmail')} wide>{form?.estEmail}</Value>
              </dl>
            ) : (
              <div className={gridCls}>
                {field('fiche-est-nom', m('contactName'), 'estName')}
                {field('fiche-est-telephone', m('contactPhone'), 'estPhone', { type: 'tel' })}
                <div className="sm:col-span-2">
                  {field('fiche-est-courriel', m('contactEmail'), 'estEmail', { type: 'email' })}
                </div>
              </div>
            )}
          </Section>

          <Section title={m('sectionCompany')}>
            {isView ? (
              <dl className={gridCls}>
                <Value label={m('cityOrAddress')} wide>{form?.address}</Value>
                <Value label={m('rbqNumber')}>{form?.rbq_number}</Value>
                <Value label={m('rbqExpiry')}>{day(form?.rbq_expiry)}</Value>
                <Value label={m('taxId')}>{form?.tax_id}</Value>
                <Value label={m('insuranceExpiry')}>{day(form?.insurance_expiry)}</Value>
              </dl>
            ) : (
              <div className={gridCls}>
                <div className="sm:col-span-2">
                  {field('fiche-adresse', m('cityOrAddress'), 'address', { placeholder: m('addressPlaceholder') })}
                </div>
                {field('fiche-rbq', m('rbqNumber'), 'rbq_number', { placeholder: 'Ex: 8361-5678-01' })}
                {field('fiche-rbq-fin', m('rbqExpiry'), 'rbq_expiry', { type: 'date' })}
                {field('fiche-neq', m('taxId'), 'tax_id')}
                {field('fiche-assurance-fin', m('insuranceExpiry'), 'insurance_expiry', { type: 'date' })}
              </div>
            )}
          </Section>

          <Section title={m('sectionDocuments')}>
            <div className={gridCls}>
              <DocUploadWidget
                label={m('rbqDoc')}
                docPath={form?.rbq_doc_url}
                signedUrl={rbqSignedUrl}
                onUpload={(file) => onDocUpload('rbq', file)}
                onRemove={() => onDocRemove('rbq')}
                uploading={uploadingRbq}
                readOnly={isView}
              />
              <DocUploadWidget
                label={m('insuranceDoc')}
                docPath={form?.insurance_doc_url}
                signedUrl={insuranceSignedUrl}
                onUpload={(file) => onDocUpload('insurance', file)}
                onRemove={() => onDocRemove('insurance')}
                uploading={uploadingInsurance}
                readOnly={isView}
              />
            </div>
          </Section>

          <Section title={m('notes')}>
            {isView ? (
              <p className={`${textCls} whitespace-pre-wrap break-words`}>
                {form?.notes || <span className={mutedCls}>—</span>}
              </p>
            ) : (
              <textarea
                id="fiche-notes"
                aria-label={m('notes')}
                value={form?.notes ?? ''}
                disabled={saving}
                onChange={(e) => onChange('notes', e?.target?.value)}
                className={textareaCls}
                rows={3}
                placeholder={m('notesPlaceholder')}
              />
            )}
          </Section>

          <Section title={m('sectionRating')}>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  disabled={isView || saving}
                  onClick={() => onChange('rating', form?.rating === star ? 0 : star)}
                  className={`transition-colors ${isView ? 'cursor-default' : ''}`}
                >
                  <Icon
                    name="Star"
                    size={20}
                    className={star <= (form?.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                  />
                </button>
              ))}
              {form?.rating > 0 && <span className={`${noteCls} ml-2`}>{form?.rating}/5</span>}
            </div>
          </Section>

          <Section
            title={m('sectionContacts')}
            action={
              isView ? null : (
                <button
                  type="button"
                  onClick={onAddContact}
                  className="text-xs leading-4 text-primary hover:text-primary-dark font-medium flex items-center gap-1 flex-shrink-0"
                >
                  <Icon name="Plus" size={13} />
                  {m('addContact')}
                </button>
              )
            }
          >
            {shownContacts?.length === 0 ? (
              <p className={noteCls}>{m('noAdditionalContacts')}</p>
            ) : isView ? (
              <div className="space-y-3">
                {shownContacts?.map((contact, index) => {
                  const roleKey = CONTACT_ROLES?.find((r) => r?.value === contact?.role)?.key;
                  return (
                    <dl key={contact?.id || index} className={gridCls}>
                      <Value label={m('contactRole')}>{roleKey ? t(`contactRoles.${roleKey}`) : contact?.role}</Value>
                      <Value label={m('contactName')}>{contact?.name}</Value>
                      <Value label={m('contactPhone')}>{contact?.phone}</Value>
                      <Value label={m('contactEmail')}>{contact?.email}</Value>
                    </dl>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {contacts?.map((contact, index) => {
                  if (contact?._delete) return null;
                  return (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-3">
                        <select
                          value={contact?.role}
                          onChange={(e) => onUpdateContact(index, 'role', e?.target?.value)}
                          className={inputCls}
                        >
                          {CONTACT_ROLES?.map((r) => (
                            <option key={r?.value} value={r?.value}>{t(`contactRoles.${r?.key}`)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={contact?.name}
                          onChange={(e) => onUpdateContact(index, 'name', e?.target?.value)}
                          className={inputCls}
                          placeholder={m('contactName')}
                        />
                      </div>
                      <div className="col-span-3">
                        <input
                          type="email"
                          value={contact?.email}
                          onChange={(e) => onUpdateContact(index, 'email', e?.target?.value)}
                          className={inputCls}
                          placeholder={m('contactEmail')}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="tel"
                          value={contact?.phone}
                          onChange={(e) => onUpdateContact(index, 'phone', e?.target?.value)}
                          className={inputCls}
                          placeholder={m('contactPhone')}
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => onRemoveContact(index)}
                          className="text-[#E53E3E] opacity-70 hover:opacity-100 transition-opacity"
                        >
                          <Icon name="Trash2" size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* ── Pied fixe ── */}
        {isView ? (
          <div className={`flex justify-end gap-2 border-t ${lineCls} px-6 py-3`}>
            <button type="button" onClick={onClose} className={outlineButtonCls}>
              {m('close')}
            </button>
            <button type="button" onClick={onEdit} className={primaryButtonCls}>
              <Icon name="Pencil" size={13} />
              <span>{m('edit')}</span>
            </button>
          </div>
        ) : (
          <div className={`flex justify-end gap-2 border-t ${lineCls} px-6 py-3`}>
            <button type="button" onClick={onCancel} disabled={saving} className={outlineButtonCls}>
              {m('cancel')}
            </button>
            <button type="button" onClick={onSave} disabled={saving} className={primaryButtonCls}>
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : null}
              <span>{record ? m('save') : m('add')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubcontractorCard;
